"use client"

import {
    Briefcase,
    CheckCircle,
    Trophy,
    BarChart,
    BriefcaseBusinessIcon,
    TrendingDown,
    ClipboardList,
} from "lucide-react"
import CountUp from "react-countup"
import { useGetAnalyticsQuery } from "../../redux/api/profile.api"
import { useNavigate } from "react-router-dom"

interface Props {
    isDashboarad?: boolean
}

export const DashboardAnalyticsCard = ({ isDashboarad = false }: Props) => {
    const { data: statistics, error, isLoading } = useGetAnalyticsQuery()

    if (isLoading) {
        return (
            <div className="flex h-[35vh] items-center overflow-hidden py-2 justify-center min-w-max  bg-white dark:bg-[#1a1a1a]/90">
                <div className="w-12 h-12 border-4 border-t-transparent border-accent-500 rounded-full animate-spin"></div>
            </div>
        )
    }

    if (error) {
        return (
            <div className="p-4 bg-red-50 text-red-500 rounded-lg border border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800">
                <div className="flex items-center gap-6">
                    <p>Error: {error.message}</p>
                    <button
                        onClick={() => {
                            window.location.reload()
                        }}
                        className="flex items-center gap-2 px-4 py-2 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition duration-200"
                    >
                        <span className="text-sm">Retry</span>
                    </button>
                </div>
            </div>
        )
    }

    const stats = [
        {
            label: "Total Interviews",
            value: statistics?.totalInterviews,
            icon: <Briefcase className="h-6 w-6" />,
            color: "from-blue-500 to-blue-600",
            bgColor: "bg-blue-50 dark:bg-blue-900/20",
            textColor: "text-blue-600 dark:text-blue-400",
            description: "Practice sessions completed",
            insight:
                statistics?.totalInterviews ? (statistics?.totalInterviews > 10 ? "Great practice consistency!" : "Keep practicing to improve your skills") : "",
            tip: "Aim for at least 2-3 practice sessions per week",
        },
        {
            label: "Job Positions",
            value: statistics?.positions,
            icon: <BriefcaseBusinessIcon className="h-6 w-6" />,
            color: "from-orange-500 to-orange-600",
            bgColor: "bg-orange-50 dark:bg-orange-900/20",
            textColor: "text-orange-600 dark:text-orange-400",
            description: "Different roles practiced",
            insight: statistics?.positions ? (statistics?.positions > 5 ? "Diverse preparation approach!" : "Consider practicing for more role types") : "",
            tip: "Practice for various positions to broaden your skills",
        },
        {
            label: "Completed Sessions",
            value: statistics?.completed,
            icon: <CheckCircle className="h-6 w-6" />,
            color: "from-green-500 to-green-600",
            bgColor: "bg-green-50 dark:bg-green-900/20",
            textColor: "text-green-600 dark:text-green-400",
            description: "Successfully finished interviews",
            insight: !!statistics?.totalInterviews ?
                statistics?.completed === statistics?.totalInterviews
                    ? "Perfect completion rate!"
                    : "Focus on completing all started sessions" : "No Insights available",
            tip: "Complete every session to maximize learning",
        },
        {
            label: "Highest Score",
            value: statistics?.maxScore,
            isPercent: true,
            icon: <Trophy className="h-6 w-6" />,
            color: "from-yellow-500 to-yellow-600",
            bgColor: "bg-yellow-50 dark:bg-yellow-900/20",
            textColor: "text-yellow-600 dark:text-yellow-400",
            description: "Your best performance",
            insight: statistics?.maxScore ?
                (statistics?.maxScore >= 80
                    ? "Excellent peak performance!"
                    : statistics?.maxScore >= 60
                        ? "Good progress, aim higher!"
                        : "Keep practicing to improve") : "",
            tip: "Analyze your best sessions to replicate success",
        },
        {
            label: "Average Score",
            value: statistics?.avgScore,
            isPercent: true,
            icon: <BarChart className="h-6 w-6" />,
            color: "from-purple-500 to-purple-600",
            bgColor: "bg-purple-50 dark:bg-[#1a1a1a]/20",
            textColor: "text-purple-600 dark:text-purple-400",
            description: "Overall performance level",
            insight: statistics?.avgScore ? (statistics?.avgScore >= 70
                ? "Strong consistent performance!"
                : statistics?.avgScore >= 50
                    ? "Steady improvement needed"
                    : "Focus on fundamentals") : "",
            tip: "Consistency is key to interview success",
        },
        {
            label: "Lowest Score",
            value: statistics?.minScore,
            isPercent: true,
            icon: <TrendingDown className="h-6 w-6" />,
            color: "from-red-500 to-red-600",
            bgColor: "bg-red-50 dark:bg-red-900/20",
            textColor: "text-red-600 dark:text-red-400",
            description: "Area needing improvement",
            insight: statistics?.minScore ? (statistics?.minScore >= 40 ? "Good baseline performance" : "Identify weak areas for focused practice") : "",
            tip: "Review your lowest-scoring sessions for improvement areas",
        },
    ]

    // const getPerformanceTrend = () => {
    //     if (!statistics?.maxScore || !statistics?.minScore || !statistics?.avgScore) return null

    //     const range = Math.floor(statistics.maxScore - statistics.minScore)
    //     const consistency = range <= 20 ? "high" : range <= 40 ? "moderate" : "low"

    //     return {
    //         consistency,
    //         range,
    //         isImproving: statistics.avgScore > (statistics.minScore + statistics.maxScore) / 2,
    //     }
    // }

    // const performanceTrend = getPerformanceTrend()
    if (!statistics?.totalInterviews)
        return (
            <EmptyInterviewState />
        )


    return (
        <div className="space-y-6">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-[#1a1a1a]/90 dark:text-white mb-2"> Dashboard</h1>
                <p className="text-gray-600 dark:text-gray-400">
                    Track your progress and improve your interview skills with detailed insights and actionable tips
                </p>
            </div>
            {/* {performanceTrend && (
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-[#1a1a1a]/20 dark:to-[#2a2a2a]/20 rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50">
                    <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-gray-100 dark:bg-[#1a1a1a]/40 rounded-xl flex items-center justify-center">
                            {performanceTrend.isImproving ? (
                                <TrendingUp className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
                            ) : (
                                <Info className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
                            )}
                        </div>
                        <div className="flex-1">
                            <h3 className="text-lg font-semibold text-[#1a1a1a]/90 dark:text-white mb-2">Performance Overview</h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                                Your performance shows{" "}
                                <span className="font-medium text-indigo-600 dark:text-indigo-400">
                                    {performanceTrend.consistency} consistency
                                </span>{" "}
                                with a {performanceTrend.range}% score range.
                                {performanceTrend.isImproving
                                    ? " You're showing positive improvement trends!"
                                    : " Focus on consistent practice to improve your average."}
                            </p>
                            <div className="flex flex-wrap gap-2">
                                <span
                                    className={`px-3 py-1 rounded-full text-xs font-medium ${performanceTrend.consistency === "high"
                                        ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                                        : performanceTrend.consistency === "moderate"
                                            ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400"
                                            : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                                        }`}
                                >
                                    {performanceTrend.consistency.charAt(0).toUpperCase() + performanceTrend.consistency.slice(1)}{" "}
                                    Consistency
                                </span>
                                {performanceTrend.isImproving && (
                                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                                        Improving Trend
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )} */}

            <div
                className={`w-full  grid grid-cols-1 gap-6  mobile:grid-cols-2 lg:grid-cols-3 `}
            >
                {
                    stats.map((item, idx) => (
                        <div
                            key={idx}
                            className="group bg-gradient-to-br from-white to-gray-50/50 dark:from-[#2a2a2a] dark:to-[#1a1a1a]/90/50 rounded-2xl p-5 border border-gray-200/50 dark:border-gray-700/50 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 backdrop-blur-sm"
                        >
                            <div className="flex items-start gap-4 mb-4">
                                <div
                                    className={`w-12 h-12 ${item.bgColor} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}
                                >
                                    <div className={item.textColor}>{item.icon}</div>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-baseline gap-1 mb-1">
                                        <span className="text-2xl font-bold text-[#1a1a1a]/90 dark:text-white tabular-nums">
                                            {<CountUp end={item.value || 0} duration={2.5} separator="," />}
                                        </span>
                                        {item.isPercent && !!item.value && (
                                            <span className="text-lg font-semibold text-gray-600 dark:text-gray-400">%</span>
                                        )}
                                    </div>
                                    <p className="text-sm font-medium text-[#1a1a1a]/90 dark:text-white mb-1">{item.label}</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">{item.description}</p>
                                </div>
                            </div>

                            <div className="space-y-3">
                                <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                                    <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Insight</p>
                                    <p className="text-xs text-gray-600 dark:text-gray-400">{item.insight || "No insights available"}</p>
                                </div>

                                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800/30">
                                    <p className="text-xs font-medium text-blue-700 dark:text-blue-300 mb-1">💡 Tip</p>
                                    <p className="text-xs text-blue-600 dark:text-blue-400">{item.tip || ""}</p>
                                </div>
                            </div>

                            {/* Progress indicator for percentage values */}
                            {item.isPercent && !!item.value && (
                                <div className="mt-4 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 overflow-hidden">
                                    <div
                                        className={`h-full bg-gradient-to-r ${item.color} rounded-full transition-all duration-1000 ease-out`}
                                        style={{ width: `${Math.min(item.value, 100)}%` }}
                                    />
                                </div>
                            )}
                        </div>
                    ))}
            </div>
        </div>
    )
}


function EmptyInterviewState() {
    const navigate = useNavigate()
    return (
        <div className="flex items-center justify-center min-h-[400px]  py-6">
            <div className="w-full ">
                <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white p-8 shadow-sm transition-all hover:shadow-md dark:border-gray-800 dark:bg-[#242424]">
                    {/* Decorative background gradient */}
                    <div className="absolute -right-12 -top-12 h-32 w-32 rounded-full bg-accent-500/10 blur-3xl" />
                    <div className="absolute -bottom-8 -left-8 h-24 w-24 rounded-full bg-accent-500/5 blur-2xl" />

                    <div className="relative flex flex-col items-center text-center">
                        {/* Icon container */}
                        <div className="mb-6 flex h-10 w-10 items-center justify-center rounded-2xl bg-accent-500/10 ring-8 ring-accent-500/5">
                            <ClipboardList className="h-8 w-8 text-accent-500" strokeWidth={1.5} />
                        </div>

                        {/* Heading */}
                        <h3 className="mb-2 text-2xl font-semibold tracking-tight text-gray-900 dark:text-gray-100">
                            No Interviews Yet
                        </h3>

                        {/* Description */}
                        <p className="mb-8 max-w-sm text-balance text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                            Start your first quick interview to gather insights and track your analytics. It only takes a few minutes
                            to get started.
                        </p>

                        {/* CTA Button */}
                        <button
                            onClick={() => navigate('/interview-setup')}
                            className="group relative inline-flex items-center justify-center gap-2 overflow-hidden rounded-xl bg-accent-500 px-6 py-3 font-medium text-white shadow-lg shadow-accent-500/25 transition-all hover:bg-accent-600 hover:shadow-xl hover:shadow-accent-500/30 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span className="relative z-10">Start Quick Interview</span>
                            <svg
                                className="relative z-10 h-4 w-4 transition-transform group-hover:translate-x-0.5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                            </svg>

                            {/* Button shine effect */}
                            <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent transition-transform duration-500 group-hover:translate-x-full" />
                        </button>

                        {/* Optional secondary action */}
                        <button className="mt-4 text-sm font-medium text-gray-500 transition-colors hover:text-accent-500 dark:text-gray-400 dark:hover:text-accent-400">
                            Learn more about interviews
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}